<template>
  <!-- Not playable — show the fallback card without an API call -->
  <div v-if="isUnsupported" class="embed-player embed-player--fallback">
    <div class="embed-player__unsupported">
      <div class="embed-player__unsupported-badge">
        <v-icon :size="36" :icon="fallbackIcon" />
      </div>
      <span class="embed-player__unsupported-text">{{ t('fallback.noPreview') }}</span>
    </div>
  </div>

  <!-- Playable — load and render player -->
  <div
    v-else
    ref="embedRoot"
    :class="['embed-player', resolvedTypeClass, hasStripClass, { 'embed-player--fallback': showFallbackCard }]"
    :style="embedThemeStyle"
    :data-embed-uid="embedId"
  >
    <!-- Custom CSS injection (from embed preset) — scoped to this embed instance -->
    <component :is="'style'" v-if="scopedEmbedCss" type="text/css">{{ scopedEmbedCss }}</component>
    <!-- Loading state -->
    <div v-if="embedLoader.isLoading.value" class="embed-player__loading">
      <v-progress-circular indeterminate size="32" width="3" color="primary" />
    </div>

    <!-- The load failed: what it means to a visitor, never the API's sentence -->
    <PlayerRefusal
      v-else-if="embedLoader.failure.value"
      :failure="embedLoader.failure.value"
      :can-retry="!!props.embedId"
      @retry="retry"
    />

    <!-- Video collection — a shell that mounts the player each item needs, so a
         collection may mix hosted videos and provider videos (YouTube, …). -->
    <CollectionEmbed
      v-else-if="resolvedType === 'video' && isCollection"
      ref="activeEmbed"
      :embed="embedLoader.embed.value as Embed<AssetListItem>"
      :start-index="startPending ? startIndex : 0"
      :viewer-id="viewerId"
      :auto-play="autoPlay"
      :auto-play-next="autoPlayNext"
      :show-ads="showAds"
      :loop="loop"
      :muted="muted"
      :controls="controls"
      :pin-controls="pinControls"
      :show-playlist="showPlaylist"
      :collection-display-mode="collectionDisplayMode"
      :enable-download="enableDownload"
      :show-chapters="showChapters"
      :show-eco-mode="showEcoMode"
      :eco-mode-default="ecoModeDefault"
      :subtitles-auto-start="subtitlesAutoStart"
      :show-info-button="showInfoButton"
      :enable-pip="enablePip"
      :enable-fullscreen="enableFullscreen"
      :show-homepage-info="showHomepageInfo"
      :show-homepage-play-button="showHomepagePlayButton"
      :poster-fit="posterFit"
      :start-at="startAt"
      :theme="resolvedTheme === 'colorful' ? 'dark' : resolvedTheme"
      :show-logo="showLogo"
      :logo-url="resolvedLogoUrl"
      :logo-position="logoPosition"
      :logo-click-url="logoClickUrl"
      :primary-color="primaryColor"
      :custom-css="customCss"
      :seeking="seeking"
      @item-change="(index: number, item: unknown) => $emit('itemChange', index, item)"
      @play="$emit('play')"
      @pause="$emit('pause')"
      @ended="$emit('ended')"
      @error="$emit('error', $event)"
      @time-update="$emit('timeUpdate', $event)"
    />

    <!-- Video -->
    <VideoEmbed
      v-else-if="resolvedType === 'video'"
      ref="activeEmbed"
      :embed="embedLoader.embed.value as Embed<VideoAssetListItem>"
      :viewer-id="viewerId"
      :auto-play="autoPlay"
      :auto-play-next="autoPlayNext"
      :show-ads="showAds"
      :loop="loop"
      :muted="muted"
      :controls="controls"
      :pin-controls="pinControls"
      :show-playlist="showPlaylist"
      :collection-display-mode="collectionDisplayMode"
      :enable-download="enableDownload"
      :show-chapters="showChapters"
      :show-eco-mode="showEcoMode"
      :eco-mode-default="ecoModeDefault"
      :subtitles-auto-start="subtitlesAutoStart"
      :show-info-button="showInfoButton"
      :enable-pip="enablePip"
      :enable-fullscreen="enableFullscreen"
      :show-homepage-info="showHomepageInfo"
      :show-homepage-play-button="showHomepagePlayButton"
      :poster-fit="posterFit"
      :start-at="startAt"
      :theme="resolvedTheme"
      :show-logo="showLogo"
      :logo-url="resolvedLogoUrl"
      :logo-position="logoPosition"
      :logo-click-url="logoClickUrl"
      :primary-color="primaryColor"
      :custom-css="customCss"
      :seeking="seeking"
      @embed-load="$emit('embedLoad', $event)"
      @item-change="(index: number, item: unknown) => $emit('itemChange', index, item)"
      @play="$emit('play')"
      @pause="$emit('pause')"
      @ended="$emit('ended')"
      @error="$emit('error', $event)"
      @time-update="$emit('timeUpdate', $event)"
    />

    <!-- Audio -->
    <AudioEmbed
      v-else-if="resolvedType === 'audio'"
      ref="activeEmbed"
      :embed="embedLoader.embed.value as Embed<AudioAssetListItem>"
      :viewer-id="viewerId"
      :auto-play="autoPlay"
      :auto-play-next="autoPlayNext"
      :loop="loop"
      :muted="muted"
      :shuffle="shuffle"
      :show-waveform="showWaveform"
      :show-chapters="showChapters"
      :show-playlist="showPlaylist"
      :enable-fullscreen="enableFullscreen"
      :show-branding="resolvedShowBranding"
      :branding-logo-url="resolvedLogoUrl"
      :start-at="startAt"
      :theme="resolvedTheme"
      :icon-color="resolvedAudioAccent"
      :seeking="seeking"
      @embed-load="$emit('embedLoad', $event)"
      @item-change="(index: number, item: unknown) => $emit('itemChange', index, item)"
      @play="$emit('play')"
      @pause="$emit('pause')"
      @ended="$emit('ended')"
      @error="$emit('error', $event)"
    />

    <!-- Image. An image or a document has no playback, and its `ended` is a moment of its viewing:
         an image once shown, a document once its last page is reached, said of one played alone
         (`../common/stillEnd.ts`). -->
    <ImageEmbed
      v-else-if="resolvedType === 'image'"
      ref="activeEmbed"
      :embed="embedLoader.embed.value as Embed<ImageAssetListItem>"
      :viewer-id="viewerId"
      :enable-download="enableDownload"
      :enable-zoom="enableZoom"
      :enable-fullscreen="enableFullscreen"
      :enable-slideshow="enableSlideshow"
      :slideshow-duration="slideshowDuration"
      :show-captions="showCaptions"
      :theme="resolvedTheme"
      :primary-color="resolvedPrimaryColor"
      :logo-url="resolvedLogoUrl"
      :logo-position="logoPosition"
      :logo-click-url="logoClickUrl"
      :enable-asset-link="enableAssetLink"
      @embed-load="$emit('embedLoad', $event)"
      @item-change="(index: number, item: unknown) => $emit('itemChange', index, item)"
      @ended="$emit('ended')"
      @error="$emit('error', $event)"
    />

    <!-- PDF/Document (only when a viewable PDF exists; non-PDF documents fall
         through to the "Preview not available" placeholder below) -->
    <PDFEmbed
      v-else-if="resolvedType === 'document' && isDocumentPreviewable"
      ref="activeEmbed"
      :embed="embedLoader.embed.value as Embed<DocumentAssetListItem>"
      :viewer-id="viewerId"
      :enable-download="enableDownload"
      :show-thumbnails="showThumbnails"
      :show-outline="showOutline"
      :start-page="startPage"
      :enable-search="enableSearch"
      :theme="resolvedTheme"
      :primary-color="resolvedPrimaryColor"
      :logo-url="resolvedLogoUrl"
      :logo-click-url="logoClickUrl"
      @embed-load="$emit('embedLoad', $event)"
      @ended="$emit('ended')"
      @error="$emit('error', $event)"
    />

    <!-- Third Party (YouTube, Vimeo, Dailymotion) -->
    <ThirdPartyEmbed
      v-else-if="resolvedType === 'third_party'"
      ref="activeEmbed"
      :embed="embedLoader.embed.value as Embed<ThirdPartyAssetListItem>"
      :viewer-id="viewerId"
      :theme="resolvedTheme"
      :auto-play="autoPlay"
      @embed-load="$emit('embedLoad', $event)"
      @error="$emit('error', $event)"
    />

    <!-- Loaded but no player for this type: compact "no preview" card -->
    <div v-else-if="embedLoader.embed.value" class="embed-player__unsupported">
      <div class="embed-player__unsupported-badge">
        <v-icon :size="36" :icon="fallbackIcon" />
      </div>
      <div v-if="fallbackName" class="embed-player__unsupported-name">{{ fallbackName }}</div>
      <span class="embed-player__unsupported-text">{{ t('fallback.noPreview') }}</span>
      <v-btn
        v-if="fallbackDownloadUrl"
        class="embed-player__unsupported-download"
        variant="flat"
        color="primary"
        size="small"
        :href="fallbackDownloadUrl"
        target="_blank"
        download
      >
        <v-icon :icon="mdiDownload" start />
        {{ t('fallback.download') }}
      </v-btn>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, nextTick, ref, toRef, watch } from 'vue'
  import { usePlayerTheme } from '../common/composables/usePlayerTheme'
  import {
    mdiDownload,
    mdiFileOutline,
    mdiFileDocumentOutline,
    mdiFolderZipOutline,
    mdiSubtitlesOutline,
  } from '@mdi/js'
  import type { Embed, PlayerTheme } from '../types/embed'
  import type { CollectionSubtype } from '../types/analytics'
  import type { PlayerError } from '../types/base'
  import type { CollectionDisplayMode } from '../video/types'
  import type {
    AssetListItem,
    VideoAssetListItem,
    AudioAssetListItem,
    ImageAssetListItem,
    DocumentAssetListItem,
    ThirdPartyAssetListItem,
  } from '../types/asset'
  import { useEmbedLoader } from '../common/composables/useEmbedLoader'
  import PlayerRefusal from '../common/components/PlayerRefusal.vue'
  import { useEmbedResize } from '../common/composables/useEmbedResize'
  import { resolveAudioAccentColor } from '../audio/utils/audio-utils'
  import VideoEmbed from '../video/components/VideoEmbed.vue'
  import CollectionEmbed from '../collection/CollectionEmbed.vue'
  import AudioEmbed from '../audio/components/AudioEmbed.vue'
  import ImageEmbed from '../image/components/ImageEmbed.vue'
  import PDFEmbed from '../pdf/components/PDFEmbed.vue'
  import ThirdPartyEmbed from '../third-party/components/ThirdPartyEmbed.vue'
  import { useEmbedPlayerI18n } from './i18n'

  const { t } = useEmbedPlayerI18n()

  const PLAYABLE_TYPES = ['video', 'audio', 'image', 'document', 'collection', 'third_party'] as const

  // Types that actually render a player component. Anything else falls back to
  // the "no preview" card (documents are conditional on having a viewable PDF).
  const RENDERABLE_TYPES = ['video', 'audio', 'image', 'third_party'] as const

  // File-type icon for the fallback card badge.
  const FALLBACK_ICONS: Record<string, string> = {
    document: mdiFileDocumentOutline,
    archive: mdiFolderZipOutline,
    subtitles: mdiSubtitlesOutline,
    file: mdiFileOutline,
    other: mdiFileOutline,
  }

  interface Props {
    /**
     * Id of the resource to load. The loader strategy is resolved by
     * `useEmbedLoader` (an injected `provideEmbedLoaderApi`, else the default
     * public embed API), so this is the embed id for embeds and the asset id for
     * the share and app players — the component stays agnostic.
     */
    embedId?: string
    /** Access token for token-required embeds */
    token?: string
    /** Link password, for a password-protected share opened through its embed page */
    password?: string
    /**
     * The preset the look is resolved with, first in the cascade: the one the embed page's URL
     * named, or the one the application previews. Read once, with the embed, as `token` is.
     */
    preset?: string
    /** Viewer ID for analytics */
    viewerId?: string
    /** Asset type hint — if provided and not playable, skips the API call */
    assetType?: string
    /** Auto-play when loaded */
    autoPlay?: boolean
    /** Auto-play next item in a collection when the current one ends */
    autoPlayNext?: boolean
    /** Play ads configured on the embed (pre-roll/mid-roll/post-roll) */
    showAds?: boolean
    /** Loop playback */
    loop?: boolean
    /** Show playlist for collections */
    showPlaylist?: boolean
    /** Enable download */
    enableDownload?: boolean
    /** Player theme — when unset, resolved from the asset type (see resolvedTheme) */
    theme?: 'dark' | 'light' | 'colorful'
    /** Custom icon color (CSS color value, audio only) */
    iconColor?: string
    /** Collection display mode */
    collectionDisplayMode?: CollectionDisplayMode
    /** Start in eco mode; undefined defers to the embed response */
    ecoModeDefault?: boolean
    /** Start the default subtitle track; undefined defers to the embed response */
    subtitlesAutoStart?: boolean
    /** Show branding logo (only for public embeds) */
    showBranding?: boolean
    /**
     * Logo à afficher, quand l'appelant en tient un que l'API ne connaît pas.
     *
     * Le logo est résolu côté serveur et n'arrive que dans la réponse d'embed.
     * Un écran de réglages doit pourtant pouvoir montrer celui qu'on vient de
     * choisir, avant tout enregistrement — d'où cette surcharge, symétrique de
     * `logoPosition` / `logoClickUrl` juste en dessous.
     */
    logoUrl?: string
    /** Show workspace logo overlay */
    showLogo?: boolean
    /** Override logo position */
    logoPosition?: 'top_left' | 'top_right' | 'bottom_left' | 'bottom_right' | 'control_bar'
    /** Override logo click URL */
    logoClickUrl?: string
    /** Custom CSS to inject into the player */
    customCss?: string
    /** Show chapter markers (video) */
    showChapters?: boolean
    /** Show eco mode button (video) */
    showEcoMode?: boolean
    /** Show info button (video) */
    showInfoButton?: boolean
    /** Enable picture-in-picture (video) */
    enablePip?: boolean
    /** Enable fullscreen (video, audio, image) */
    enableFullscreen?: boolean
    /** Shuffle mode (audio) */
    shuffle?: boolean
    /** Show waveform visualization (audio) */
    showWaveform?: boolean
    /** Enable zoom (image) */
    enableZoom?: boolean
    /** Show thumbnail sidebar (document) */
    showThumbnails?: boolean
    /** Show outline sidebar (document) */
    showOutline?: boolean
    /** How the poster thumbnail fits the player area (video) */
    posterFit?: 'contain' | 'cover'
    /** Start muted (video) */
    muted?: boolean
    /** Show player controls (video) */
    controls?: boolean
    /** Keep the control bar always visible (video, disables auto-hide) */
    pinControls?: boolean
    /** Show title/duration on homepage overlay (video) */
    showHomepageInfo?: boolean
    /** Show play button on homepage overlay (video) */
    showHomepagePlayButton?: boolean
    /** Start playback at this time in seconds (video, audio) */
    startAt?: number
    /**
     * Whether the viewer may move the playhead (video, audio) — `false` draws the bar still and
     * refuses the keyboard's and the chapters' moves. `seek` from the page that framed the player
     * is not the viewer's, and still reaches the playhead.
     */
    seeking?: boolean
    /** Enable slideshow for image collections (image) */
    enableSlideshow?: boolean
    /** Slideshow interval in seconds (image) */
    slideshowDuration?: number
    /** Show captions under images in grid layout (image) */
    showCaptions?: boolean
    /** Initial page number (document) */
    startPage?: number
    /** Enable text search (document) */
    enableSearch?: boolean
    /** Primary color override (CSS color value) */
    primaryColor?: string
    /** Enable in-app asset link (image collections, Mediasbox only) */
    enableAssetLink?: boolean
    /**
     * The entry a collection opens on, 0-based — what a channel's page asks for when the visitor
     * picked an episode before the player was mounted (the cinematic layout's modal).
     */
    startIndex?: number
  }

  const props = withDefaults(defineProps<Props>(), {
    embedId: undefined,
    token: undefined,
    password: undefined,
    preset: undefined,
    viewerId: undefined,
    assetType: undefined,
    autoPlay: false,
    autoPlayNext: true,
    showAds: true,
    loop: false,
    showPlaylist: true,
    enableDownload: false,
    theme: undefined,
    iconColor: undefined,
    collectionDisplayMode: 'drawer',
    showBranding: false,
    showLogo: true,
    logoUrl: undefined,
    logoPosition: undefined,
    logoClickUrl: undefined,
    customCss: undefined,
    ecoModeDefault: undefined,
    subtitlesAutoStart: undefined,
    showChapters: true,
    showEcoMode: true,
    showInfoButton: true,
    enablePip: true,
    enableFullscreen: true,
    shuffle: false,
    showWaveform: true,
    enableZoom: true,
    showThumbnails: false,
    showOutline: false,
    posterFit: 'contain',
    muted: false,
    controls: true,
    pinControls: false,
    showHomepageInfo: true,
    showHomepagePlayButton: true,
    startAt: undefined,
    seeking: true,
    enableSlideshow: true,
    slideshowDuration: 5,
    showCaptions: true,
    startPage: 1,
    enableSearch: true,
    primaryColor: undefined,
    enableAssetLink: false,
    startIndex: 0,
  })

  const emit = defineEmits<{
    embedLoad: [embed: Embed]
    itemChange: [index: number, item: unknown]
    play: []
    pause: []
    ended: []
    error: [error: PlayerError]
    timeUpdate: [currentTime: number]
    themeResolve: [theme: 'dark' | 'light' | 'colorful']
  }>()

  const activeEmbed = ref<InstanceType<
    | typeof CollectionEmbed
    | typeof VideoEmbed
    | typeof AudioEmbed
    | typeof ImageEmbed
    | typeof PDFEmbed
    | typeof ThirdPartyEmbed
  > | null>(null)
  const embedRoot = ref<HTMLElement | null>(null)

  const isUnsupported = computed(() => {
    if (!props.assetType) return false
    return !PLAYABLE_TYPES.includes(props.assetType.toLowerCase() as any)
  })

  // The loader strategy (default embed API, or an injected one such as the
  // share or app player loader) is resolved inside useEmbedLoader.
  const embedLoader = useEmbedLoader({
    embedId: toRef(props, 'embedId'),
    token: props.token,
    password: props.password,
    preset: props.preset,
    viewerId: props.viewerId,
    autoLoad: !isUnsupported.value,
  })

  const COLLECTION_SUBTYPE_MAP: Record<CollectionSubtype, string> = {
    videos: 'video',
    audios: 'audio',
    images: 'image',
    documents: 'document',
  }

  const playerTheme = computed<PlayerTheme | undefined>(() => {
    const base = embedLoader.embed.value?.playerTheme
    const overrides: Partial<PlayerTheme> = {}
    if (props.theme && (props.theme === 'dark' || props.theme === 'light')) {
      overrides.baseTheme = props.theme
    }
    if (props.primaryColor) overrides.primaryColor = props.primaryColor
    if (props.customCss) overrides.customCss = props.customCss
    if (Object.keys(overrides).length === 0) return base
    return { ...base, ...overrides }
  })

  // The image and document players take their colour as a prop rather than from `--vp-*`, so the
  // resolved theme's — the preset's, else the platform's — is handed over here, or they would paint
  // the host page's Vuetify primary whatever the preset says.
  const resolvedPrimaryColor = computed(() => playerTheme.value?.primaryColor || undefined)

  const resolvedLogoUrl = computed(() => {
    if (!props.showLogo) return undefined
    return props.logoUrl || embedLoader.embed.value?.playerTheme?.logoUrl
  })

  const resolvedShowBranding = computed(() => {
    if (!props.showLogo) return false
    if (props.showBranding) return true
    return embedLoader.embed.value?.playerTheme?.showBranding ?? false
  })

  // The audio player derives every accent from the one colour it is handed; unlike the video and
  // image players it does not read `--vp-primary-color` from the theme CSS. So the colour is
  // resolved here: `iconColor` (the mounted option, then the resolved player config) where it is
  // set, else `primaryColor` (the mounted option, then the theme's) like every other player —
  // otherwise the audio player samples its cover art instead of using the channel/preset color.
  const resolvedAudioAccent = computed(() =>
    resolveAudioAccentColor({
      iconColor: props.iconColor,
      configIconColor: embedLoader.embed.value?.playerConfig?.iconColor,
      primaryColor: props.primaryColor,
      themePrimaryColor: embedLoader.embed.value?.playerTheme?.primaryColor,
    }),
  )

  const { themeStyle: embedThemeStyle, customCss: themeCss } = usePlayerTheme(playerTheme)

  const scopedEmbedCss = computed(() => {
    if (!themeCss.value) return undefined
    const fontFaceRegex = /@font-face\s*\{[^}]*\}/g
    const fontFaces = themeCss.value.match(fontFaceRegex) || []
    const remainingCss = themeCss.value.replace(fontFaceRegex, '').trim()
    const scoped = remainingCss ? `[data-embed-uid="${props.embedId}"] { ${remainingCss} }` : ''
    return [fontFaces.join('\n'), scoped].filter(Boolean).join('\n')
  })

  const isCollection = computed(() => embedLoader.embed.value?.embedType === 'collection')

  const resolvedType = computed(() => {
    const embed = embedLoader.embed.value
    if (!embed) return null

    if (embed.embedType === 'collection' && embed.collection?.subtype) {
      return COLLECTION_SUBTYPE_MAP[embed.collection.subtype] ?? null
    }

    if (embed.items.length === 0) return null
    return embed.items[0].asset.type
  })

  // A `document` asset only has an in-browser viewer when a PDF exists (native
  // PDF or a converted Office rendition). csv/txt/failed conversions are not
  // previewable and fall back to the unsupported placeholder.
  const isDocumentPreviewable = computed(() => {
    const asset = embedLoader.embed.value?.items?.[0]?.asset as
      | DocumentAssetListItem
      | undefined
    return asset?.isPreviewable ?? true
  })

  // Fallback "no preview" card: a compact card with the file-type icon, the file
  // name, and a download button when the source is directly downloadable.
  const fallbackAsset = computed(
    () => embedLoader.embed.value?.items?.[0]?.asset as DocumentAssetListItem | undefined,
  )
  const fallbackName = computed(() => fallbackAsset.value?.name)
  const fallbackDownloadUrl = computed(() => fallbackAsset.value?.downloadUrl)
  const fallbackIcon = computed(() => {
    const type = (fallbackAsset.value?.type ?? props.assetType ?? '').toLowerCase()
    return FALLBACK_ICONS[type] ?? mdiFileOutline
  })

  // Whether the loaded embed has no player to render and must show the card.
  const showFallbackCard = computed(() => {
    if (!embedLoader.embed.value) return false
    const type = resolvedType.value
    if (!type) return true
    if (type === 'document') return !isDocumentPreviewable.value
    return !RENDERABLE_TYPES.includes(type as (typeof RENDERABLE_TYPES)[number])
  })

  // Theme resolution: an explicit `theme` prop always wins. Otherwise the
  // default mirrors the backend's `EmbedConfigBuilder._resolve_theme` —
  // audio → 'colorful', video → 'dark', everything else → 'light'. The
  // `assetType` hint is used while the embed is still loading.
  const resolvedTheme = computed<'dark' | 'light' | 'colorful'>(() => {
    if (props.theme) return props.theme
    const type = resolvedType.value ?? props.assetType?.toLowerCase()
    if (type === 'audio') return 'colorful'
    if (type === 'video') return 'dark'
    return 'light'
  })

  // Broadcast the resolved theme so a standalone host (the UMD embed entry)
  // can keep its Vuetify base theme in sync — 'colorful' needs a light base.
  watch(resolvedTheme, (theme) => emit('themeResolve', theme), { immediate: true })

  const resolvedTypeClass = computed(() => {
    if (resolvedType.value) return `embed-player--${resolvedType.value}`
    // Fall back to assetType prop so the correct aspect-ratio applies during loading
    if (props.assetType) {
      const normalized = props.assetType.toLowerCase()
      if (PLAYABLE_TYPES.includes(normalized as any)) {
        return `embed-player--${normalized}`
      }
    }
    return null
  })

  const hasStripClass = computed(() => {
    if (props.collectionDisplayMode === 'bottom-bar') return 'embed-player--has-strip-bottom'
    if (props.collectionDisplayMode === 'sidebar') return 'embed-player--has-strip-sidebar'
    return null
  })

  // A collection asked to open past its first entry. A video collection's shell opens there itself
  // (`start-index`): a move made once it exists would be lost while its video player waits for the
  // first entry's playback. Every other shell moves there once its player exists, through the same
  // `goToIndex` every shell exposes. Once either way, so a later reload starts where it is told to.
  const startPending = ref(props.startIndex > 0)
  watch(activeEmbed, (instance) => {
    if (!startPending.value || !instance) return
    startPending.value = false
    if (resolvedType.value === 'video' && isCollection.value) return
    void nextTick(() => (instance as { goToIndex?: (index: number) => void }).goToIndex?.(props.startIndex))
  })

  const isAudio = computed(() => resolvedType.value === 'audio')
  useEmbedResize({ element: embedRoot, embedId: props.embedId ?? '', enabled: isAudio })

  function retry(): void {
    if (props.embedId) {
      embedLoader.load(props.embedId)
    }
  }

  defineExpose({
    embed: embedLoader.embed,
    reload: () => props.embedId && embedLoader.load(props.embedId),
    goToIndex: (index: number) => (activeEmbed.value as any)?.goToIndex?.(index),
    play: () => (activeEmbed.value as any)?.play?.(),
    pause: () => (activeEmbed.value as any)?.pause?.(),
    seek: (time: number) => (activeEmbed.value as any)?.seek?.(time),
    startPlayback: () => (activeEmbed.value as any)?.startPlayback?.(),
    duration: computed(() => (activeEmbed.value as any)?.duration),
  })
</script>

<style lang="scss" scoped>
  @use '@/styles/design-tokens' as *;

  .embed-player {
    width: 100%;
    max-height: 100%;
    display: flex;
    flex-direction: column;
    background: transparent;
    border-radius: inherit;
    border: none;
    overflow: hidden;

    &--video,
    &--image,
    &--collection,
    &--third_party {
      aspect-ratio: 16 / 9;
    }

    // Standalone embed: fill the container, let <video object-fit> handle letterboxing.
    // .embed-standalone is added to the mount container by the UMD entry point.
    .embed-standalone &--video,
    .embed-standalone &--image,
    .embed-standalone &--collection,
    .embed-standalone &--third_party,
    .embed-standalone &--document {
      aspect-ratio: unset;
      height: 100%;
      max-height: none;
    }

    // Bottom-bar: inner .video-player keeps aspect-ratio 16/9 to drive the
    // height when no parent height is defined (e.g. the asset Overview tab),
    // and flex-shrink lets the video shrink when the parent imposes a height
    // that's too small for video + strip (e.g. fullscreen embed).
    &--has-strip-bottom {
      aspect-ratio: unset;
    }

    // Sidebar: video-player-main defines the height via aspect-ratio 16/9,
    // the wrapper just needs to not impose its own aspect-ratio
    &--has-strip-sidebar {
      aspect-ratio: unset;
      height: 100%;
    }

    &--audio {
      border-radius: $radius-lg;
      overflow: visible;
      max-height: none;
    }

    &--document {
      aspect-ratio: 3 / 4;
      max-height: 600px;
    }

    // The "no preview" card sizes to its content instead of a tall media box.
    &--fallback {
      aspect-ratio: unset;
      max-height: none;
    }

    &__loading,
    &__unsupported {
      flex: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 12px;
      padding: 32px 16px;
    }

    // The "no preview" card paints its own surface so it stays legible on any
    // container — the video/image player keeps its black letterbox background,
    // which is only on the media components, not on this card.
    &__unsupported {
      background: rgb(var(--v-theme-surface));
      color: rgba(var(--v-theme-on-surface), 0.6);
      min-height: 180px;
      padding: 32px 24px;
    }

    &__unsupported-badge {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 72px;
      height: 72px;
      border-radius: 18px;
      background: rgba(var(--v-theme-on-surface), 0.05);
      color: rgba(var(--v-theme-on-surface), 0.55);
    }

    &__unsupported-name {
      font-size: 15px;
      font-weight: 600;
      color: rgba(var(--v-theme-on-surface), 0.87);
      max-width: 320px;
      text-align: center;
      word-break: break-word;
    }

    &__unsupported-download {
      margin-top: 4px;
      border-radius: 8px;
    }

    &__unsupported-text {
      font-size: 13px;
      color: rgba(var(--v-theme-on-surface), 0.5);
      max-width: 300px;
      text-align: center;
    }

    &__unsupported-text {
      color: rgba(var(--v-theme-on-surface), 0.6);
    }
  }
</style>
